import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { useSeminarStore } from "../stores/seminarStore";

/**
 * App-wide data layer, extracted from the former god-component ProtectedRoute.
 * Runs purely for its side effects: it loads the current user and the seminar
 * list on auth + navigation. The seminar-list fetch goes through the store's
 * getData (the single fetch bridge — the filter actions use the same one); pages
 * read the resulting state from the feature stores/queries. (The WordPress token
 * + audiences are their own queries; see hooks/queries/useWordPress.)
 */
export const useAppData = (): void => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const { isAuth, userDetails, getUserDetails } = useAuth();

  // The seminar filter flags gate the initial auto-load below; they live in
  // useSeminarStore (their single source of truth).
  const idFilter = useSeminarStore((s) => s.idFilter);
  const dateRangeFilter = useSeminarStore((s) => s.dateRangeFilter);

  // Load the current user on mount
  useEffect(() => {
    getUserDetails();
  }, [getUserDetails]);

  // Load the seminar list once authenticated on the main view
  useEffect(() => {
    if (isAuth && userDetails) {
      if (pathname === "/seminare/semverwaltung") {
        const { getData } = useSeminarStore.getState();
        const seminarid = searchParams.get("seminarid");
        if (seminarid) {
          getData({ "filters[id]": seminarid });
          useSeminarStore.setState({ idFilter: true });
        } else {
          if (!idFilter && !dateRangeFilter) {
            getData();
          }
        }
      }
    }
  }, [isAuth, pathname, userDetails, searchParams, idFilter, dateRangeFilter]);
};

export default useAppData;
